/* eslint-disable  @typescript-eslint/no-explicit-any */
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import * as React from "react";
import type { IEnvironment } from "../../../types/EnvironmentTypes";
import { useEnvironmentService } from "../../../services/registry/EnvironmentService";
import DialogContentText from "@mui/material/DialogContentText";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import { validateIntegerField } from "../../../utils/Validation";
import { DateTime } from "luxon";

export interface EnvAutoStopConfig {
  idleStopEnabled?: boolean;
  idleStopAfterMinutes?: number;
  scheduledEnabled?: boolean;
  scheduledStartAt?: string;
  scheduledStopAt?: string;
  scheduledOnlyWeekdays?: boolean;
}

export interface EnvironmentSchedulerPopupProps {
  visible: boolean;
  environment: IEnvironment;
  onDismiss: () => void;
  onSave: (environmentName: string, scheduledConf: string) => Promise<void>;
}

export const EnvironmentSchedulerPopup = ({ visible, environment, onDismiss, onSave }: EnvironmentSchedulerPopupProps) => {
  const environmentService = useEnvironmentService();

  const [conf, setConf] = useState<EnvAutoStopConfig>({});
  const [idleStopEnabled, setIdleStopEnabled] = useState<boolean>(true);
  const [scheduledEnabled, setScheduledEnabled] = useState<boolean>(false);
  const [scheduledOnlyWeekdays, setScheduledOnlyWeekdays] = useState<boolean>(false);

  const [idleStopAfterMinutesError, setIdleStopAfterMinutesError] = useState<string | undefined>(undefined);
  const [scheduledStartAtError, setScheduledStartAtError] = useState<string | undefined>(undefined);
  const [scheduledStopAtError, setScheduledStopAtError] = useState<string | undefined>(undefined);

  useEffect(() => {
    (async () => fetchAutoStopConf())();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const fetchAutoStopConf = async () => {
    try {
      const config = await environmentService.autoStopConf(environment.name);
      setConf({
        ...config,
        scheduledStartAt: fromUTCtoLocalTime(config.scheduledStartAt),
        scheduledStopAt: fromUTCtoLocalTime(config.scheduledStopAt),
      });
      setIdleStopEnabled(config.idleStopEnabled ?? false);
      setScheduledEnabled(config.scheduledEnabled ?? false);
      setScheduledOnlyWeekdays(config.scheduledOnlyWeekdays ?? false);
    } catch (e: any) {
      console.log(e.debugMessage || "Could not fetch the schedule for environment.");
    }
  };

  const dialogCloseHandler = (_: unknown, reason: string) => {
    if (reason !== "backdropClick") {
      onDismiss();
    }
  };

  const okButtonClickHandler = async () => {
    const idleStopAfterMinutes = validateIdleStopAfterMinutes(
      conf.idleStopAfterMinutes ? conf.idleStopAfterMinutes.toString() : "",
    );
    const scheduledStartAt = validateScheduledStartAt(conf.scheduledStartAt as string);
    const scheduledStopAt = validateScheduledStopAt(conf.scheduledStopAt as string);

    if (!idleStopAfterMinutes || !scheduledStartAt || !scheduledStopAt) {
      return;
    }

    const config: EnvAutoStopConfig = {
      idleStopEnabled,
      idleStopAfterMinutes,
      scheduledEnabled,
      scheduledStartAt: fromLocalTimeToUTC(scheduledStartAt),
      scheduledStopAt: fromLocalTimeToUTC(scheduledStopAt),
      scheduledOnlyWeekdays,
    };

    await onSave(environment.name, JSON.stringify(config));
  };

  const validateStartStopFormat = (
    value: string,
    setError: Dispatch<SetStateAction<string | undefined>>,
    fieldName: string,
  ) => {
    const parts = value.split(":");

    if (parts.length !== 2) {
      setError(`${fieldName} does not contain hour and minute separated by ":"`);
      return "";
    }

    const hour = parseInt(parts[0], 10);
    const minute = parseInt(parts[1], 10);

    if (minute < 0 || minute > 59) {
      setError(`${fieldName} minute must be a value between 0 and 59`);
      return "";
    }

    if (isNaN(hour) || hour < 0 || hour > 23) {
      setError(`${fieldName} hour must be a value between 0 and 23`);
      return "";
    }

    setError(undefined);
    return value;
  };

  const idleStopAfterMinutesChangeHandler = (value: string) => validateIdleStopAfterMinutes(value);

  const validateIdleStopAfterMinutes = (value: string) =>
    validateIntegerField(value, setIdleStopAfterMinutesError, 30, 1200);

  const scheduledStartAtChangeHandler = (value: string) => validateScheduledStartAt(value);

  const validateScheduledStartAt = (value: string) => validateStartStopFormat(value, setScheduledStartAtError, "Start At");

  const scheduledStopAtChangeHandler = (value: string) => validateScheduledStopAt(value);

  const validateScheduledStopAt = (value: string) => validateStartStopFormat(value, setScheduledStopAtError, "Stop At");

  const fromUTCtoLocalTime = (time: string) => {
    const parts = time.split(":");
    const hour = parseInt(parts[0], 10);
    const minute = parseInt(parts[1], 10);

    const ts = DateTime.utc().set({ hour, minute }).toLocal();

    const paddedHour = ts.hour.toString().padStart(2, "0");
    const paddedMinute = ts.minute.toString().padStart(2, "0");

    return `${paddedHour}:${paddedMinute}`;
  };

  const fromLocalTimeToUTC = (time: string) => {
    const parts = time.split(":");
    const hour = parseInt(parts[0], 10);
    const minute = parseInt(parts[1], 10);

    const ts = DateTime.now().set({ hour, minute }).toUTC();

    const paddedHour = ts.hour.toString().padStart(2, "0");
    const paddedMinute = ts.minute.toString().padStart(2, "0");

    return `${paddedHour}:${paddedMinute}`;
  };

  const checkboxStyle = {
    display: "flex",
    flex: 1,
    flexDirection: "row" as const,
    alignItems: "center",
    marginTop: 12,
    marginLeft: -12,
  };

  return (
    <Dialog open={visible} onClose={dialogCloseHandler}>
      <DialogTitle>{`Update schedule for ${environment.name}`}</DialogTitle>
      <DialogContent>
        <DialogContentText>Please update the information below:</DialogContentText>
        <div style={checkboxStyle}>
          <Checkbox color="primary" checked={idleStopEnabled} onChange={() => setIdleStopEnabled(!idleStopEnabled)} />
          <Typography variant="body2">Idle auto-stop enabled</Typography>
        </div>
        <TextField
          autoFocus
          margin="dense"
          fullWidth
          variant="standard"
          id="name"
          label="Stop after idle for (minutes)"
          type="number"
          value={conf?.idleStopAfterMinutes ?? ""}
          error={idleStopAfterMinutesError !== undefined}
          helperText={idleStopAfterMinutesError}
          onChange={(e) => {
            setConf({ ...conf, idleStopAfterMinutes: Number(e.target.value) });
            idleStopAfterMinutesChangeHandler(e.target.value);
          }}
          disabled={!idleStopEnabled}
        />
        <div style={checkboxStyle}>
          <Checkbox color="primary" checked={scheduledEnabled} onChange={() => setScheduledEnabled(!scheduledEnabled)} />
          <Typography variant="body2">Scheduled auto-stop/start enabled</Typography>
        </div>
        <TextField
          autoFocus
          margin="dense"
          fullWidth
          variant="standard"
          id="name"
          label="Start At (HH:MM)"
          type="text"
          value={conf?.scheduledStartAt ?? ""}
          error={scheduledStartAtError !== undefined}
          helperText={scheduledStartAtError}
          onChange={(e) => {
            setConf({ ...conf, scheduledStartAt: e.target.value });
            scheduledStartAtChangeHandler(e.target.value);
          }}
          disabled={!scheduledEnabled}
        />
        <TextField
          autoFocus
          margin="dense"
          fullWidth
          variant="standard"
          id="name"
          label="Stop At At (HH:MM)"
          type="text"
          value={conf?.scheduledStopAt ?? ""}
          error={scheduledStopAtError !== undefined}
          helperText={scheduledStopAtError}
          onChange={(e) => {
            setConf({ ...conf, scheduledStopAt: e.target.value });
            scheduledStopAtChangeHandler(e.target.value);
          }}
          disabled={!scheduledEnabled}
        />
        <div style={checkboxStyle}>
          <Checkbox
            color="primary"
            checked={scheduledOnlyWeekdays}
            onChange={() => setScheduledOnlyWeekdays(!scheduledOnlyWeekdays)}
          />
          <Typography variant="body2">Scheduled only on weekdays</Typography>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onDismiss}>Cancel</Button>
        <Button onClick={okButtonClickHandler}>OK</Button>
      </DialogActions>
    </Dialog>
  );
};
